import React, { useEffect, useState } from 'react'
import { FaPlus, FaEdit, FaEye } from 'react-icons/fa'
import { RiDeleteBin6Line } from 'react-icons/ri'
import { Table, Form, Input, Upload, message, Tooltip } from 'antd'
import { DatabaseZap, Search } from 'lucide-react'
import { translations, imageTypes, pageSizeOptions } from '../../utils/constants'
import { DeleteModal, CustomModal } from '../../Components'
import { UploadOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { POST } from '../../utils/apis'
import { secureApi } from '../../Config/api'
import { fetchOrganization, verifyToken, getBase64, fetchAllOrganization } from '../../utils/helpers'
import { setOrganizationPageNumber, setOrganizationsData } from '../../Redux/actions/organizationActions'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'

const initials = (name) => String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '-'

const Organization = ({ language }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector((state) => state?.authReducer?.user)
  const filerOrganizationFromRedux = useSelector((state) => state.organizationReducer?.organizations)
  const organizationFromRedux = useSelector((state) => state.organizationReducer?.allOrganizations)
  // Persisted redux state from before pageNumber existed restores it as
  // undefined, which the API rejected (LIMIT NaN) and the list came back empty.
  const currentPage = useSelector(state => state.organizationReducer?.pageNumber) || 1
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [organizations, setOrganizations] = useState([])
  const [viewModal, setViewModal] = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [addDataLoading, setAdDataLoading] = useState(false)
  const [logoUrl, setLogoUrl] = useState(null)
  const [orgToDelete, setOrgToDelete] = useState(null)
  const [editDataLoading, setEditDataLoading] = useState(false)
  const [form] = Form.useForm()
  const [editOrgForm] = Form.useForm()
  const [logoFile, setLogoFile] = useState(null)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')

  // console.log(currentPage)

  useEffect(() => {
    verifyToken(history, dispatch)
    fetchAllOrganization({ dispatch })
    fetchOrganization({ setOrganizations, setOrganizationsData, dispatch, currentPage, pageSize, setTotalRecords, searchQuery })
  }, [])
  useEffect(() => {
    fetchAllOrganization({ dispatch })
    fetchOrganization({ setOrganizations, setOrganizationsData, dispatch, currentPage, pageSize, setTotalRecords, searchQuery })
  }, [currentPage, searchQuery])

  const showModal = () => {
    setIsModalVisible(true)
    setLogoFile(null)
    setLogoUrl(null)
  }

  const viewDetails = (record) => {
    setSelectedOrg(record)
    setViewModal(true)
  }

  const closeDetails = () => {
    setViewModal(false)
    setSelectedOrg(null)
  }

  const closeEditModal = () => {
    editOrgForm.resetFields()
    setEditModal(false)
  }

  const handleClose = () => {
    form.resetFields()
    setLogoFile(null)
    setLogoUrl(null)
    setIsModalVisible(false)
  }

  const handleEdit = async (record) => {
    const isTokenValid = await verifyToken(history, dispatch);
    if (!isTokenValid) {
      message.error('Session expired. Please log in again.');
      return;
    }
    setEditModal(true)
    setLogoFile(null)
    setLogoUrl(null)
    setViewModal(false)
    setSelectedOrg(record)
    editOrgForm.setFieldsValue({
      org_name: record?.organization_name,
      website: record?.website,
      client: record?.client,
    })
    setLogoUrl(record?.logo)
  }

  const handleDelete = async (record) => {
    const isTokenValid = await verifyToken(history, dispatch);
    if (!isTokenValid) {
      message.error('Session expired. Please log in again.');
      return;
    }
    setDeleteModal(true)
    setOrgToDelete(record)
  }

  const handleCancel = () => {
    setDeleteModal(false)
    setOrgToDelete(null)
    setLogoFile(null)
    setLogoUrl(null)
  }

  const getFileList = () => {
    if (logoUrl) {
      return [
        {
          uid: '-1',
          name: 'logo.png',
          status: 'done',
          url: logoUrl,
        },
      ];
    }
    return [];
  };

  const handleUpload = (file) => {
    setLogoUrl(null)
    getBase64(file)
      .then((base64) => {
        setLogoFile(file)
        setLogoUrl(base64)
      })
    return false
  }

  const handleAddOrganization = async () => {
    const isTokenValid = await verifyToken(history, dispatch);
    if (!isTokenValid) {
      message.error('Session expired. Please log in again.');
      return;
    }
    try {
      const values = await form.validateFields()

      if (!logoFile) {
        message.error("Please Upload a Logo");
        return;
      }
      const formData = new FormData()
      formData.append('files', logoFile)
      setAdDataLoading(true)

      const uploadResponse = await secureApi.post(POST?.UPLOAD_FILE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      const urls = uploadResponse?.fileUrls?.map((file) => file) || []

      const response = await secureApi.post(POST?.ADD_ORGANIZATION, {
        userId: user.id,
        organizationName: values?.org_name,
        website: values?.website,
        client: values?.client,
        logo: urls?.[0],
      })
      if (response?.success == true) {
        setIsModalVisible(false)
        form.resetFields()
        setLogoFile(null)
        setLogoUrl(null)
        fetchAllOrganization({ dispatch })
        fetchOrganization({ setOrganizations, setOrganizationsData, dispatch, currentPage, pageSize, setTotalRecords, searchQuery })
      }
      else if (response?.success === false) {
        message.error(response?.error || response?.message)
      }
    } catch (error) {
      // console.error('Error adding organization:', error)
    }
    finally {
      setAdDataLoading(false)
    }

  }

  const handleUpdateOrganization = async () => {
    setEditDataLoading(true)
    try {
      const values = await editOrgForm.validateFields()
      if (!logoUrl && !logoFile) {
        message.error("Please upload a logo")
        setEditDataLoading(false)
        return
      }
      const formData = new FormData()
      if (logoFile) {
        formData.append('files', logoFile)
      }
      let logoUrls = selectedOrg?.logo || []
      if (logoFile) {
        const uploadResponse = await secureApi.post(POST?.UPLOAD_FILE, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        logoUrls = uploadResponse?.fileUrls?.map((file) => file) || []
      }
      const response = await secureApi.post(POST?.UPDATE_ORGANIZATION, {
        id: selectedOrg?.id,
        organizationName: values?.org_name,
        website: values?.website,
        client: values?.client,
        logo: logoUrls,
      })

      if (response?.success === true) {
        message.success('Organization updated successfully')
        setLogoFile(null)
        setLogoUrl(null)
        setEditModal(false)
        editOrgForm.resetFields()
        fetchAllOrganization({ dispatch })
        fetchOrganization({ setOrganizations, setOrganizationsData, dispatch, currentPage, pageSize, setTotalRecords, searchQuery })
      } else if (response?.success === false) {
        message.error(response?.error || response?.message)
      }
    } catch (error) {
      // console.error('Error updating organization:', error)
      message.error('Failed to update organization. Please try again.')
    }
    finally {
      setEditDataLoading(false)
    }
  }

  const handleDeleteOrganization = async () => {
    const isTokenValid = await verifyToken(history, dispatch)

    if (!isTokenValid) {
      message.error('Session expired. Please log in again.')
      return
    }

    setDeleteModal(false)

    if (orgToDelete) {
      try {
        const response = await secureApi?.post(POST?.DELETE_ORGANIZATION, {
          organizationId: orgToDelete?.id,
        })
        if (response?.success === true) {
          message.success('Organization deleted successfully')
          const updatedOrganizations = organizations?.filter((org) => org?.id !== orgToDelete?.id)
          dispatch(setOrganizationsData(updatedOrganizations))
          fetchAllOrganization({ dispatch })
          fetchOrganization({ setOrganizations, setOrganizationsData, dispatch, currentPage, pageSize, setTotalRecords, searchQuery })
        } else {
          message.error(response.data?.message || 'Failed to delete organization.')
        }
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to delete organization. Please try again.')
      }
    }

    setOrgToDelete(null)
  }

  const columns = [
    {
      title: translations[language]?.orgName,
      dataIndex: 'organization_name',
      key: 'organization_name',
      render: (text, record) => (
        <div className='vl-name-cell' onClick={() => viewDetails(record)}>
          <span className='vl-avatar'>
            {record?.logo ? <img src={record.logo} alt='' /> : initials(text)}
          </span>
          <span className='vl-name-text capitalized-name'>{text}</span>
          <FaEye className='vl-name-view' />
        </div>
      ),
    },
    {
      title: translations[language]?.website,
      dataIndex: 'website',
      key: 'website',
      render: (text) => (text ? text : <span className='vl-muted'>-</span>),
    },
    {
      title: translations[language]?.client,
      dataIndex: 'client',
      key: 'client',
      render: (text) => (text ? text : <span className='vl-muted'>-</span>),
    },
    {
      title: translations[language]?.totalUsers,
      dataIndex: 'total_user',
      key: 'total_user',
      render: (text) => (
        <span className={`vl-count-chip${!Number(text) ? ' vl-count-chip-zero' : ''}`}>{text ?? 0}</span>
      ),
    },
    {
      title: translations[language]?.action,
      key: 'action',
      render: (_, record) => (
        <div className='vl-actions'>
          <Tooltip title={translations[language]?.edit}>
            <button type='button' className='vl-action-btn vl-action-edit' onClick={() => handleEdit(record)}>
              <FaEdit size={14} />
            </button>
          </Tooltip>
          <Tooltip title={translations[language]?.delete}>
            <button type='button' className='vl-action-btn vl-action-delete' onClick={() => handleDelete(record)}>
              <RiDeleteBin6Line size={15} />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ]

  const handleSearch = (query) => {
    setSearchQuery(query)
  }

  return (
    <div className="main-container">
      <div className="main-inner-container">
        <h1 className="main-heading">{translations[language]?.organizations}</h1>
        <div className="insider-container">
          {organizationFromRedux?.length === 0 ?
            null
            :
            <div className="insider-container-buttons organization-header">
              <div>
                <span className="add-button" onClick={showModal}>
                  <FaPlus className="icon" />
                  {translations[language]?.addOrganization}
                </span>
              </div>
              <div className="search-main-div organization-search-div">
                <div className="search-inner-div organization-search-input">
                  <Search className="search-icon" />
                  <input
                    placeholder={translations[language]?.searchOrganizations}
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="search-input"
                  />
                  {searchQuery && (
                    <span className="clear-icon" style={{ cursor: "pointer" }} onClick={() => handleSearch("")}>
                      ✕
                    </span>
                  )}
                </div>
              </div>

            </div>
          }
          <div className='vd-card'>
            {organizationFromRedux?.length === 0 ? (
              <div className='vd-card-body'>
                <div className="no-vehicles-container">
                  <div className="no-vehicles-text-center">
                    <div className="flex-col-center">
                      <div className="no-vehicles-text-center">
                        <DatabaseZap className="no-vehicle-icon" />
                        <h3 className="no-vehicles-heading">
                          {translations[language]?.orgNotFound}
                        </h3>
                        <p className="no-vehicles-description">
                          {translations[language]?.addOrgToSeeItsList}
                        </p>
                        <div>
                          <span className="add-button" onClick={showModal}>
                            <FaPlus className="icon" />
                            {translations[language]?.addOrganization}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className='vd-card-header'>
                  <div className='vl-list-title'>
                    <h2 className='vd-card-title'>{translations[language]?.organizationList}</h2>
                    <span className='vl-total-chip'>{translations[language]?.totalRecord}: <strong>{totalRecords}</strong></span>
                  </div>
                </div>
                <div className='vd-card-body'>
                  <Table
                    className='vl-table'
                    dataSource={filerOrganizationFromRedux}
                    columns={columns}
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      total: totalRecords,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      pageSizeOptions,
                      onChange: (page, size) => {
                        dispatch(setOrganizationPageNumber(page))
                        setPageSize(size)
                        fetchOrganization({ setOrganizations, setOrganizationsData, dispatch, page, pageSize: size, setTotalRecords, searchQuery })
                      },
                      onShowSizeChange: (current, size) => {
                        setPageSize(size)
                        dispatch(setOrganizationPageNumber(1))
                        fetchOrganization({ setOrganizations, setOrganizationsData, dispatch, page: 1, pageSize: size, setTotalRecords, searchQuery })
                      }
                    }}
                    scroll={{ x: 'max-content' }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <CustomModal
        isVisible={isModalVisible}
        onClose={handleClose}
        title={translations[language]?.addOrganization}
        actionText={translations[language]?.addOrganization}
        onAction={handleAddOrganization}
        loading={addDataLoading}
        language={language}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={translations[language]?.orgName}
            name="org_name"
            rules={[{ required: true, message: translations[language]?.pleaseEnterOrgName }]}
          >
            <Input placeholder={translations[language]?.enterOrgName} style={{ height: 40 }} />
          </Form.Item>
          <Form.Item
            label={translations[language]?.website}
            name="website"
          >
            <Input placeholder={translations[language]?.enterWebsite} style={{ height: 40 }} />
          </Form.Item>
          <Form.Item
            label={translations[language]?.client}
            name="client"
            rules={[{ required: true, message: translations[language]?.pleaseEnterClient }]}
          >
            <Input placeholder={translations[language]?.client} style={{ height: 40 }} />
          </Form.Item>
          <Form.Item name="files" label={translations[language]?.logo} rules={[{ required: true, message: translations[language]?.fileRequired || 'Files is required' }]}>
            <Upload
              listType="picture-card"
              maxCount={1}
              multiple={false}
              fileList={getFileList()}
              beforeUpload={handleUpload}
              onRemove={() => setLogoUrl(null)}
              accept={imageTypes}
            >
              <div>
                <UploadOutlined /> {translations[language]?.uploadLogo}
              </div>
            </Upload>
          </Form.Item>
        </Form>
      </CustomModal>

      <CustomModal
        isVisible={editModal}
        onClose={closeEditModal}
        title={translations[language]?.editOrgText}
        actionText={translations[language]?.updateOrganization}
        onAction={handleUpdateOrganization}
        loading={editDataLoading}
        language={language}
      >
        <Form form={editOrgForm} layout="vertical">
          <Form.Item
            label={translations[language]?.orgName}
            name="org_name"
            rules={[{ required: true, message: translations[language]?.pleaseEnterOrgName }]}
          >
            <Input placeholder={translations[language]?.enterOrgName} style={{ height: 40 }} />
          </Form.Item>
          <Form.Item
            label={translations[language]?.website}
            name="website"
          >
            <Input placeholder={translations[language]?.enterWebsite} style={{ height: 40 }} />
          </Form.Item>
          <Form.Item
            label={translations[language]?.client}
            name="client"
            rules={[{ required: true, message: translations[language]?.pleaseEnterClient }]}
          >
            <Input placeholder={translations[language]?.client} style={{ height: 40 }} />
          </Form.Item>
          <Form.Item name="files" label={translations[language]?.logo} rules={[{ required: true, message: translations[language]?.fileRequired || 'Files is required' }]}>
            <Upload
              listType="picture-card"
              maxCount={1}
              multiple={false}
              beforeUpload={handleUpload}
              onRemove={() => setLogoUrl(null)}
              fileList={logoUrl ? [{ url: String(logoUrl) }] : []}
              accept={imageTypes}
            >
              <div>
                <UploadOutlined /> {translations[language]?.uploadLogo}
              </div>
            </Upload>
          </Form.Item>
        </Form>
      </CustomModal>

      <CustomModal
        isVisible={viewModal}
        onClose={closeDetails}
        title={translations[language]?.orgDetailsHeading}
        actionText={translations[language]?.editOrgText}
        onAction={() => handleEdit(selectedOrg)}
        language={language}
      >
        <div>
          <div className='device-information-section'>
            <div className='inner-information-div'>
              <div className='info-childern-div'>
                <div className='inner-information-childern'>
                  <h3 className="info-heading">{translations[language]?.orgName}</h3>
                  <p className='capitalized-name'>{selectedOrg?.organization_name}</p>
                </div>
              </div>
              <div className='info-childern-div'>
                <div className='inner-information-childern'>
                  <h3 className="info-heading">{translations[language]?.website}</h3>
                  <p>{selectedOrg?.website || '-'}</p>
                </div>
              </div>
            </div>
            <div className='inner-information-div'>
              <div className='info-childern-div'>
                <div className='inner-information-childern'>
                  <h3 className="info-heading">{translations[language]?.client}</h3>
                  <p>{selectedOrg?.client || '-'}</p>
                </div>
              </div>
              <div className='info-childern-div'>
                <div className='inner-information-childern'>
                  <h3 className="info-heading">{translations[language]?.totalUsers}</h3>
                  <p>{selectedOrg?.total_user || 0}</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </CustomModal>

      <DeleteModal
        title={translations[language]?.deleteOrganization}
        bodyText={
          <>
            {translations[language]?.areYouSureYouWantToDltThisOrganization}
          </>
        }
        isOpen={deleteModal}
        onCancel={handleCancel}
        onDelete={handleDeleteOrganization}
        language={language}
      />
    </div>
  )
}

export default Organization